import { z } from 'zod';
import { CODIGO_RECEITA_RE, tituloDaGuia } from './codigos-receita.js';

/**
 * Regras puras da guia, usadas por API, worker e tela. Nada aqui é gravado: a situação é função do que está na
 * entrega (pagamento, vencimento, "pagar até", cancelamento, substituição) e do dia de hoje.
 */
export const SituacaoGuia = z.enum(['A_VENCER', 'VENCE_HOJE', 'VENCIDA', 'PAGA', 'CANCELADA', 'SUBSTITUIDA']);
export type SituacaoGuia = z.infer<typeof SituacaoGuia>;
export const ROTULO_SITUACAO_GUIA: Record<SituacaoGuia, string> = { A_VENCER: 'a vencer', VENCE_HOJE: 'vence hoje', VENCIDA: 'vencida', PAGA: 'paga', CANCELADA: 'cancelada', SUBSTITUIDA: 'substituída' };

/** Um tributo dentro do DARF numerado (DCTFWeb junta vários num documento só). */
export const ComposicaoItem = z.object({ codigo: z.string().trim().max(10), nome: z.string().max(120).nullable().default(null), valor: z.number().nullable().default(null) });
export type ComposicaoItem = z.infer<typeof ComposicaoItem>;

export interface DadosGuia {
  dataPagamento?: string | null;
  /** vencimento legal */
  vencimentoGuia?: string | null;
  /** até quando o valor vale (DARF consolidado com multa e juros); quando existe, é o que o cliente vê */
  pagarAte?: string | null;
  canceladaEm?: string | null;
  substituidaPorId?: string | null;
}

/** A data que manda: "pagar até" quando houver, senão o vencimento. */
export const dataLimiteGuia = (g: DadosGuia): string | null => g.pagarAte ?? g.vencimentoGuia ?? null;

export function situacaoDaGuia(g: DadosGuia, hoje: string): SituacaoGuia {
  if (g.canceladaEm) return 'CANCELADA';
  if (g.substituidaPorId) return 'SUBSTITUIDA';
  if (g.dataPagamento) return 'PAGA';
  const limite = dataLimiteGuia(g);
  if (!limite) return 'A_VENCER';
  return limite < hoje ? 'VENCIDA' : limite === hoje ? 'VENCE_HOJE' : 'A_VENCER';
}

/** Dias até o limite (negativo = já passou). */
export function diasParaLimite(g: DadosGuia, hoje: string): number | null {
  const limite = dataLimiteGuia(g);
  if (!limite) return null;
  return Math.round((Date.UTC(Number(limite.slice(0, 4)), Number(limite.slice(5, 7)) - 1, Number(limite.slice(8, 10))) - Date.UTC(Number(hoje.slice(0, 4)), Number(hoje.slice(5, 7)) - 1, Number(hoje.slice(8, 10)))) / 86_400_000);
}

/**
 * A data mostrada ao cliente para um documento: a da guia (pagar até, senão vencimento) quando o arquivo é de uma guia;
 * senão o vencimento da tarefa. Uma regra só para ficha, e-mail, portal e lembrete.
 */
export function vencimentoExibido(entrega: DadosGuia | null | undefined, vencimentoTarefa: string | null | undefined): string | null {
  return (entrega ? dataLimiteGuia(entrega) : null) ?? vencimentoTarefa ?? null;
}

/**
 * Nome da guia como o cliente vê: o título dado na tarefa; senão pelo tipo e código de receita
 * ("DARF 8741 — CIDE importação"); senão pela sigla da obrigação ("DAS", "GUIA DE FGTS").
 */
export function nomeDaGuia(e: { titulo?: string | null; tipoGuia?: string | null; codigoReceita?: string | null }, siglaObrigacao?: string | null): string {
  const t = e.titulo?.trim();
  if (t) return t;
  const cod = e.codigoReceita?.trim();
  return tituloDaGuia(e.tipoGuia, cod && CODIGO_RECEITA_RE.test(cod) ? cod : null, siglaObrigacao);
}

/** Rótulo de competência "09/2026" a partir da chave 202609. */
export const rotuloCompetenciaGuia = (chave: number | null | undefined): string | null => (chave ? `${String(chave % 100).padStart(2, '0')}/${Math.floor(chave / 100)}` : null);

// ---------------------------------------------------------------- pagamento: nível de evidência (fatia 2a)

/**
 * O que se sabe do pagamento, além de "paga" (`dataPagamento` continua a única verdade de PAGA — `situacaoDaGuia` não
 * conhece nada disto): NENHUMA (a pagar) · INFORMADA (o cliente avisou, pelo portal ou pelo link) · CONFIRMADA (o escritório
 * conferiu o aviso do cliente) · CONTESTADA (o escritório não encontrou o pagamento: a data voltou a nula e a próxima tentativa
 * exige comprovante) · REGISTRADA (o escritório marcou, ou registro anterior a este controle — origem nula nunca é reclassificada).
 */
export const EvidenciaPagamento = z.enum(['NENHUMA', 'INFORMADA', 'CONFIRMADA', 'CONTESTADA', 'REGISTRADA']);
export type EvidenciaPagamento = z.infer<typeof EvidenciaPagamento>;
export const ROTULO_EVIDENCIA_PAGAMENTO: Record<EvidenciaPagamento, string> = { NENHUMA: 'a pagar', INFORMADA: 'informada pelo cliente', CONFIRMADA: 'confirmada', CONTESTADA: 'pagamento não encontrado', REGISTRADA: 'paga' };

type Instante = string | Date | null | undefined;
const ms = (v: Instante): number | null => (v == null ? null : v instanceof Date ? v.getTime() : Date.parse(v));
export interface DadosPagamento {
  dataPagamento?: Instante;
  /** PORTAL | LINK (o cliente) · ESCRITORIO (a equipe) · nulo (registro anterior) */
  pagamentoOrigem?: string | null;
  pagamentoInformadoEm?: Instante;
  pagamentoConfirmadoEm?: Instante;
  pagamentoContestadoEm?: Instante;
}
export const ORIGENS_CLIENTE = ['PORTAL', 'LINK'] as const;
export function evidenciaPagamento(e: DadosPagamento): EvidenciaPagamento {
  const informado = ms(e.pagamentoInformadoEm);
  if (e.dataPagamento) {
    if (!(ORIGENS_CLIENTE as readonly string[]).includes(e.pagamentoOrigem ?? '')) return 'REGISTRADA';
    const confirmado = ms(e.pagamentoConfirmadoEm);
    // confirmação de um aviso anterior (o cliente avisou de novo depois) não vale para este
    return confirmado != null && (informado == null || confirmado >= informado) ? 'CONFIRMADA' : 'INFORMADA';
  }
  const contestado = ms(e.pagamentoContestadoEm);
  return contestado != null && (informado == null || informado <= contestado) ? 'CONTESTADA' : 'NENHUMA';
}

export interface DadosPagavel extends DadosGuia {
  /** há multa ou juros registrados (não pelas datas: pelo valor) */
  comMultaJuros?: boolean | null;
  /** BOLETO vencido continua se pagando pela mesma linha (o banco cobra os juros); DAS/DARF/FGTS precisam de recálculo */
  tipoGuia?: string | null;
}
/** Guia já vencida mas registrada com multa e juros e sem "pague até" distinto: é a consolidada — vai em "para pagar" com aviso, não em "vencida". */
export const ehConsolidada = (g: DadosPagavel, hoje: string): boolean => situacaoDaGuia(g, hoje) === 'VENCIDA' && !!g.comMultaJuros && !(g.pagarAte && g.vencimentoGuia && g.pagarAte !== g.vencimentoGuia);
/**
 * Dá para pagar com os dados dela (linha digitável, código de barras, PIX)? Viva, não paga e com o valor ainda válido: a vencer, vence
 * hoje, consolidada — ou boleto bancário (vencido, o banco cobra juros na mesma linha). Guia de arrecadação vencida de verdade precisa
 * de recálculo — nada de copiar a linha dela.
 */
export function ehPagavel(g: DadosPagavel, hoje: string): boolean {
  const s = situacaoDaGuia(g, hoje);
  if (s === 'PAGA' || s === 'CANCELADA' || s === 'SUBSTITUIDA') return false;
  return s !== 'VENCIDA' || ehConsolidada(g, hoje) || g.tipoGuia === 'BOLETO';
}
